import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage, languageName } from '../context/LanguageContext';
import RouvyScraperModal from './RouvyScraperModal';
import ActivitiesModal from './ActivitiesModal';
import RouvyDomainHint from './RouvyDomainHint';
import rouvyLogo from '../rouvy.svg';
import { localeFor } from '../utils/timeFormat';
import { getCountryFlag as findCountryFlag, getCountryName as findCountryName } from '../utils/countries';

// Чат живет на отдельном поддомене, отдельным приложением
const CHAT_URL = process.env.REACT_APP_CHAT_URL || 'https://chat.cyclingvirtualleague.com';

// Пока профиль открыт, счётчик чата перезапрашивается с этим шагом
const CHAT_UNREAD_POLL_MS = 60 * 1000;

const WELCOME_VISIBLE_MS = 60 * 1000;
// Совпадает с длительностью transition у .profile-welcome в App.css
const WELCOME_FADE_MS = 800;

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [rouvyConnected, setRouvyConnected] = useState(false);
  const [rouvyLoading, setRouvyLoading] = useState(false);
  const [rouvySyncing, setRouvySyncing] = useState(false);
  const [rouvyError, setRouvyError] = useState('');
  // ROUVY с 19.08.2026 проверяет OAuth-скоупы: старым токенам не хватает прав
  const [rouvyNeedsReauth, setRouvyNeedsReauth] = useState(false);
  const [showScraperModal, setShowScraperModal] = useState(false);
  const [showActivitiesModal, setShowActivitiesModal] = useState(false);
  const [accountActionLoading, setAccountActionLoading] = useState(false);
  const [accountActionError, setAccountActionError] = useState('');
  // Приветствие: 'visible' → через минуту 'hiding' (CSS-анимация) → 'hidden' (убрано из DOM)
  const [welcomeState, setWelcomeState] = useState('visible');
  const [chatUnread, setChatUnread] = useState(0);
  const fileInputRef = useRef(null);

  // Непрочитанные в чате. Сессия общая, поэтому хватает токена из localStorage.
  // Запрос идёт мимо axios: его интерцептор на 401 разлогинивает из CVL, а
  // недоступный или незнакомый чату токен не повод выкидывать из профиля.
  // Пока страница открыта, счётчик обновляется по таймеру
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || user?.deactivated) return undefined;

    let aborter = null;
    const load = () => {
      // Предыдущий запрос мог не успеть завершиться к следующему тику
      if (aborter) aborter.abort();
      aborter = new AbortController();

      fetch(`${CHAT_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: aborter.signal,
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => setChatUnread(data?.unread_notifications || 0))
        .catch(() => {
          // Чат может быть недоступен или отвечать ошибкой — профиль это не ломает
        });
    };

    load();
    const timer = setInterval(load, CHAT_UNREAD_POLL_MS);

    return () => {
      clearInterval(timer);
      if (aborter) aborter.abort();
    };
  }, [user?.deactivated]);

  // Убираем по таймеру, а не по transitionend: там, где grid-template-rows
  // не анимируется (Safari < 16), событие не приходит и блок остался бы в DOM
  React.useEffect(() => {
    if (welcomeState === 'hidden') return undefined;

    const timer = welcomeState === 'visible'
      ? setTimeout(() => setWelcomeState('hiding'), WELCOME_VISIBLE_MS)
      : setTimeout(() => setWelcomeState('hidden'), WELCOME_FADE_MS);
    return () => clearTimeout(timer);
  }, [welcomeState]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeactivateAccount = async () => {
    if (!window.confirm(t('profile.deactivateConfirm'))) return;

    setAccountActionLoading(true);
    setAccountActionError('');
    try {
      const response = await axios.post('/api/users/deactivate');
      updateUser(response.data);
    } catch (error) {
      console.error('Deactivate account error:', error);
      setAccountActionError(t('profile.deactivateFailed'));
    } finally {
      setAccountActionLoading(false);
    }
  };

  const handleReactivateAccount = async () => {
    setAccountActionLoading(true);
    setAccountActionError('');
    try {
      const response = await axios.post('/api/users/reactivate');
      updateUser(response.data);
    } catch (error) {
      console.error('Reactivate account error:', error);
      setAccountActionError(t('profile.reactivateFailed'));
    } finally {
      setAccountActionLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Проверка типа файла
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError(t('profile.invalidFileType'));
      return;
    }

    // Проверка размера файла (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(t('profile.fileTooLarge'));
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post('/api/upload/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Обновляем пользователя с новым аватаром
      const updatedUser = { ...user, avatar: response.data.url };
      updateUser(updatedUser);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
      // Сбрасываем input чтобы можно было загрузить тот же файл снова
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Проверяем подключение к ROUVY при загрузке
  React.useEffect(() => {
    checkRouvyConnection();
    
    // Обрабатываем URL параметры после OAuth callback
    const rouvyConnected = searchParams.get('rouvy_connected');
    const rouvyError = searchParams.get('rouvy_error');
    
    if (rouvyConnected === 'success') {
      const message = t('rouvy.connectedSuccess');
      alert(message);
      
      checkRouvyConnection(); // Обновляем статус подключения
      setRouvySyncing(true); // Показываем индикатор синхронизации
      
      // Начинаем опрос статуса синхронизации
      pollSyncStatus();
      
      // Очищаем URL параметры
      setSearchParams({});
    } else if (rouvyError) {
      const errorMessage = decodeURIComponent(rouvyError.replace(/\+/g, ' '));
      setRouvyError(errorMessage);
      // Очищаем URL параметры
      setSearchParams({});
    }
  }, [searchParams, t]);

  const checkRouvyConnection = async () => {
    try {
      const response = await axios.get('/api/rouvy/status');
      setRouvyConnected(response.data.connected);
      // needs_reauthorization — токен выдан без нужных прав (см. плашку ниже)
      setRouvyNeedsReauth(Boolean(response.data.needs_reauthorization));
    } catch (error) {
      console.error('Failed to check ROUVY connection:', error);
      setRouvyConnected(false);
      setRouvyNeedsReauth(false);
    }
  };

  // Переподключение — это обычная авторизация: callback перезапишет токен новыми правами.
  // Отключать интеграцию заранее нельзя — если пользователь не дойдет до конца,
  // он останется совсем без ROUVY
  const reconnectRouvy = () => connectToRouvy();

  const connectToRouvy = async () => {
    setRouvyLoading(true);
    setRouvyError('');
    
    try {
      // Получаем URL для OAuth авторизации
      const response = await axios.get('/api/rouvy/authorize');
      
      if (response.data.authorization_url) {
        // Перенаправляем пользователя на страницу авторизации ROUVY
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('ROUVY authorization error:', error);
      const errorMessage = error.response?.data?.error || 
        t('rouvy.authUrlError');
      setRouvyError(errorMessage);
      setRouvyLoading(false);
    }
  };

  const disconnectFromRouvy = async () => {
    const confirmMessage = t('rouvy.disconnectConfirm');
    
    if (!window.confirm(confirmMessage)) return;

    setRouvyLoading(true);
    setRouvyError('');
    try {
      await axios.delete('/api/rouvy/disconnect');
      setRouvyConnected(false);
      alert(t('rouvy.disconnected'));
    } catch (error) {
      console.error('ROUVY disconnect error:', error);
      setRouvyError(t('rouvy.disconnectError'));
    } finally {
      setRouvyLoading(false);
    }
  };

  const syncWithRouvy = async () => {
    if (!rouvyConnected) {
      setRouvyError(t('rouvy.connectFirst'));
      return;
    }

    setRouvySyncing(true);
    setRouvyError('');

    try {
      const response = await axios.post('/api/rouvy/sync_profile');
      
      // Backend теперь ставит задачу в очередь и возвращает статус 202
      if (response.status === 202) {
        const message = t('rouvy.syncStarted');
        alert(message);
        
        // Начинаем опрос статуса и обновление данных
        pollSyncStatus();
      }
    } catch (error) {
      console.error('ROUVY sync error:', error);
      const errorMessage = error.response?.data?.error || 
        t('rouvy.syncError');
      setRouvyError(errorMessage);
      setRouvySyncing(false);
    }
  };

  const pollSyncStatus = () => {
    let attempts = 0;
    const maxAttempts = 10;
    const interval = 3000; // 3 секунды

    const pollInterval = setInterval(async () => {
      attempts++;

      try {
        const statusResponse = await axios.get('/api/rouvy/status');
        const lastSynced = new Date(statusResponse.data.last_synced_at);
        const now = new Date();
        const secondsSinceSync = (now - lastSynced) / 1000;

        // Если синхронизация была в последние 30 секунд, считаем что она завершена
        if (secondsSinceSync < 30) {
          clearInterval(pollInterval);
          await refreshUserData();
          setRouvySyncing(false);
          
          const message = t('rouvy.syncSuccess');
          alert(message);
        }

        // Прекращаем опрос после максимального количества попыток
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          // Все равно обновляем данные
          await refreshUserData();
          setRouvySyncing(false);
        }
      } catch (error) {
        console.error('Error polling sync status:', error);
        // Продолжаем опрос даже при ошибке
      }
    }, interval);
  };

  const refreshUserData = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      updateUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const handleScraperSuccess = (profileData) => {
    // Обновляем данные пользователя после успешной синхронизации через скрапер
    const updatedUser = { 
      ...user
    };
    
    // Если передан has_rouvy_credentials, обновляем его
    if (profileData.hasOwnProperty('has_rouvy_credentials')) {
      updatedUser.has_rouvy_credentials = profileData.has_rouvy_credentials;
    }
    
    // Обновляем rouvy_profile_data только если есть данные профиля
    if (profileData && Object.keys(profileData).length > 0 && !profileData.hasOwnProperty('has_rouvy_credentials')) {
      // Создаем копию данных без поля avatar для rouvy_profile_data
      const { avatar, ...rouvyData } = profileData;
      
      updatedUser.rouvy_profile_data = {
        ...(user.rouvy_profile_data || {}),
        ...rouvyData
      };
      
      // Если был обновлен аватар, обновляем его в профиле пользователя
      if (avatar) {
        updatedUser.avatar = avatar;
      }
    }
    
    updateUser(updatedUser);
  };

  if (!user) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return t('signup.notAvailable');
    return new Date(dateString).toLocaleDateString(localeFor(language), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getGenderLabel = (gender) => {
    if (!gender) return '';
    return t(`profile.${gender}`);
  };

  const getCountryFlag = (countryValue) => findCountryFlag(countryValue);

  // Название на текущем языке; незнакомое значение показывается как есть
  const getCountryName = (countryValue) => findCountryName(countryValue, language);

  return (
    <div className="profile-container">
      {!user.deactivated && (
        <>
          <button onClick={() => setShowActivitiesModal(true)} className="btn btn-activities">
            {t('profile.myActivities')}
          </button>
          <button onClick={() => navigate('/workouts')} className="btn btn-activities btn-workouts">
            {t('workouts.navShort')}
          </button>
          {/* Без target="_blank": ссылку, открываемую в новом контексте, браузер
              не отдает установленному приложению чата и всегда показывает вкладкой */}
          <a
            href={CHAT_URL}
            className="chat-icon-link"
            title={t('profile.chatTitle')}
            aria-label={t('profile.chatTitle')}
          >
            <span className="chat-icon-bubble">{t('profile.chatWord')}</span>
            {chatUnread > 0 && (
              <span className="chat-icon-badge">{chatUnread > 99 ? '99+' : chatUnread}</span>
            )}
          </a>
        </>
      )}
      <div className="profile-header">
        <div className="profile-top-section">
        <div className="avatar-container" onClick={handleAvatarClick}>
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={t('a11y.profileAvatar')}
              className="profile-avatar"
              onError={(e) => {
                console.error('Failed to load avatar:', user.avatar);
                e.target.style.display = 'none';
                const placeholder = e.target.nextSibling;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          {!user.avatar && (
            <div className="profile-avatar profile-avatar-placeholder">
              {user.first_name[0]}{user.last_name[0]}
            </div>
          )}
          <div className="avatar-overlay">
            {uploading ? t('profile.uploading') : t('profile.changeAvatar')}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {uploadError && (
          <div style={{ color: 'var(--c-e53e3e-fg)', marginTop: '10px', fontSize: '14px' }}>
            {uploadError}
          </div>
        )}
        {user.ftp && (
          <div className="ftp-display">
            <div className="ftp-label">FTP</div>
            <div className="ftp-value">{user.ftp} {t('profile.watts')}</div>
            {user.weight && (
              <div className="ftp-ratio">
                {(user.ftp / user.weight).toFixed(2)} {t('profile.wattsPerKg')}
              </div>
            )}
          </div>
        )}
        </div>
        <h1 className="profile-name">
          {language !== 'ru' && user.first_name_eng && user.last_name_eng
            ? `${user.first_name_eng} ${user.last_name_eng}`
            : `${user.first_name} ${user.last_name}`}
        </h1>
        <p className="profile-nickname">@{user.nickname}</p>
      </div>

      {/* Отключенному аккаунту раздел предложений недоступен — не зовем туда */}
      {!user.deactivated && welcomeState !== 'hidden' && (
        <div
          className={`profile-welcome ${welcomeState === 'hiding' ? 'profile-welcome-hiding' : ''}`}
        >
          <div className="profile-welcome-inner">
            <p className="profile-welcome-text">{t('profile.welcomeMessage')}</p>
          </div>
        </div>
      )}

      {user.deactivated && (
        <div style={{
          backgroundColor: 'var(--c-fde2e1-bg)',
          border: '1px solid var(--c-f5b5b1-bd)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
        }}>
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--c-9b1c1c-fg)', fontSize: '15px', fontWeight: '600' }}>
            {t('profile.deactivatedTitle')}
          </h4>
          <p style={{ margin: 0, color: 'var(--c-7a1414-fg)', fontSize: '14px', lineHeight: '1.5' }}>
            {t('profile.deactivatedDescription')}
          </p>
        </div>
      )}

      {!user.deactivated && (!user?.social?.email || user?.social?.email === 'nil') && (
        <div style={{
          backgroundColor: 'var(--c-fef3cd-bg)',
          border: '1px solid var(--c-ffeaa7-bd)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          <svg 
            style={{ flexShrink: 0, marginRight: '12px', marginTop: '2px' }}
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="#f59e0b"
          >
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: 'var(--c-92400e-fg)', fontSize: '15px', fontWeight: '600' }}>
              {t('profile.emailRequiredTitle')}
            </h4>
            <p style={{ margin: 0, color: 'var(--c-78350f-fg)', fontSize: '14px', lineHeight: '1.5' }}>
              {t('profile.emailRequiredText')}
            </p>
          </div>
        </div>
      )}

      <div className="profile-info">
        {user.gender && (
          <div className="info-item">
            <span className="info-label">{t('profile.gender')}</span>
            <span className="info-value">
              {getGenderLabel(user.gender)}
            </span>
          </div>
        )}

        {user.birth_date && (
          <div className="info-item">
            <span className="info-label">{t('profile.birthDate')}</span>
            <span className="info-value">
              {new Date(user.birth_date).toLocaleDateString(
                localeFor(language),
                { year: 'numeric', month: 'long', day: 'numeric' }
              )}
            </span>
          </div>
        )}

        {user.weight && (
          <div className="info-item">
            <span className="info-label">{t('profile.weight')}</span>
            <span className="info-value">{user.weight} kg</span>
          </div>
        )}

        {user.country && (
          <div className="info-item">
            <span className="info-label">{t('profile.country')}</span>
            <span className="info-value">
              {getCountryFlag(user.country) && (
                <span style={{ fontSize: '18px', marginRight: '8px' }}>
                  {getCountryFlag(user.country)}
                </span>
              )}
              {getCountryName(user.country)}
            </span>
          </div>
        )}

        <div className="info-item">
          <span className="info-label">{t('profile.language')}</span>
          <span className="info-value">{languageName(user.language)}</span>
        </div>

        {user.last_login_at && (
          <div className="info-item">
            <span className="info-label">{t('profile.lastLogin')}</span>
            <span className="info-value">{formatDate(user.last_login_at)}</span>
          </div>
        )}
      </div>

      {!user.deactivated && (
      <div className="rouvy-section">
        <h2 className="rouvy-title" style={{ '--rouvy-logo': `url(${rouvyLogo})` }}>
          {t('rouvy.integration')}
        </h2>
        
        {rouvyConnected && rouvyNeedsReauth && (
          <div className="rouvy-reauth-notice">
            <div className="rouvy-reauth-title">
              ⚠️ {t('rouvy.reauthTitle')}
            </div>
            <p className="rouvy-reauth-text">
              {t('rouvy.reauthText')}
            </p>
            {/* Переподключение идет через тот же Authorize, что и первое
                подключение, поэтому предупреждение про домен .ru нужно и здесь */}
            <RouvyDomainHint className="rouvy-reauth-hint" />
            <button
              onClick={reconnectRouvy}
              disabled={rouvyLoading}
              className="btn btn-primary"
            >
              {rouvyLoading ? t('rouvy.connecting') : t('rouvy.reconnect')}
            </button>
          </div>
        )}

        {rouvyConnected ? (
          <div className="rouvy-connected">
            <div className="rouvy-status status-connected">
            </div>

            <div className="rouvy-actions">
              <button 
                onClick={syncWithRouvy} 
                disabled={rouvySyncing}
                className="btn btn-primary"
              >
                {rouvySyncing ? t('rouvy.syncing') : t('rouvy.sync')}
              </button>
              
              <button 
                onClick={disconnectFromRouvy} 
                disabled={rouvyLoading}
                className="btn btn-danger"
              >
                {t('rouvy.disconnect')}
              </button>
            </div>
            
            <p className="rouvy-description">
              {t('rouvy.syncDescription')}
            </p>
          </div>
        ) : (
          <div className="rouvy-disconnected">
            <div className="rouvy-status status-disconnected">
            </div>
            
            <p className="rouvy-description">
              {t('rouvy.connectDescription')}
            </p>

            {/* ROUVY возвращает часть пользователей на домен .ru, откуда redirect
                в приложение не срабатывает — предупреждаем до начала авторизации */}
            <RouvyDomainHint className="rouvy-auth-hint" />

            <button 
              onClick={connectToRouvy} 
              disabled={rouvyLoading}
              className="btn btn-primary"
            >
              {rouvyLoading ? t('rouvy.connecting') : t('rouvy.connectButton')}
            </button>
          </div>
        )}
        
        {rouvyError && (
          <div className="rouvy-error">
            {rouvyError}
          </div>
        )}
      </div>
      )}

      <RouvyScraperModal
        isOpen={showScraperModal}
        onClose={() => setShowScraperModal(false)}
        onSuccess={handleScraperSuccess}
        shouldConnect={!rouvyConnected}
        userData={user}
      />

      <ActivitiesModal 
        isOpen={showActivitiesModal}
        onClose={() => setShowActivitiesModal(false)}
        userId={user.id}
      />

      {accountActionError && (
        <div style={{ color: 'var(--c-e53e3e-fg)', marginBottom: '12px', fontSize: '14px' }}>
          {accountActionError}
        </div>
      )}

      <div className="profile-actions">
        {!user.deactivated && (
          <button onClick={() => navigate('/edit-profile')} className="btn btn-primary">
            {t('profile.editProfile')}
          </button>
        )}
        <button onClick={handleLogout} className="btn btn-secondary">
          {t('auth.logout')}
        </button>
        {user.deactivated ? (
          <button onClick={handleReactivateAccount} disabled={accountActionLoading} className="btn btn-primary">
            {t('profile.reactivateAccount')}
          </button>
        ) : (
          <button onClick={handleDeactivateAccount} disabled={accountActionLoading} className="btn btn-danger">
            {t('profile.deactivateAccount')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Profile;